import type { UIMatch } from "@remix-run/react";
import { useMatches } from "@remix-run/react";
import i18n from "~/i18n";
import { useTranslation as useTranslationBase } from "react-i18next";
import isPresent from "~/kernel/validation/isPresent";

type Options = {
  count?: number;
};

export default function useTranslation(scope?: string) {
  const namespaces = useNamespaces();
  const { ready, t, i18n } = useTranslationBase(namespaces);

  /** @deprecated use scope as arg instead **/
  const getScopedT =
    (path: string) =>
    (key: string, variables = {}) =>
      t(`${path}.${key}`, { ns: namespaces, ...variables });

  return {
    ready,
    exists: (key: string) => i18n.exists(key),
    locale: i18n.language,
    getScopedT,
    modelName: (modelName: string, options: Options = {}) => {
      return t(`${modelName}.name`, {
        ns: "models",
        count: options?.count ?? 1,
      });
    },
    enumValue: (modelName: string, attrName: string, value: string) => {
      return t(`${modelName}.values.${attrName}.${value}`, { ns: "models" });
    },
    modelAttribute: (
      modelName: string,
      attrName: string,
      options: Options = {},
    ) => {
      return t(`${modelName}.attributes.${attrName}`, {
        ns: "models",
        count: options.count ?? 1,
      });
    },
    tt: (key: string, variables = {}) => {
      if (scope) {
        return t(`${scope}.${key}`, { ns: namespaces, ...variables });
      }

      return t(key, { ns: namespaces, ...variables });
    },
    t: (key: string, variables = {}) => {
      return t(key, { ns: namespaces, ...variables });
    },
  };
}

function useNamespaces() {
  const matches = useMatches() as UIMatch<
    unknown,
    { i18n?: string | string[] } | undefined
  >[];
  const matchesNs = matches
    .map((match) => match.handle)
    .filter(isPresent)
    .flatMap((handle) => handle.i18n ?? []);
  return [...i18n.defaultNS, ...matchesNs];
}
